import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DTDashboardCreateDialog from '../dashboard/DTDashboardCreateDialog';

/**
 * Handles both single-component and multi-component binding scenarios.
 *
 * Props:
 *  componentIds    - string[]  - list of selected component IDs
 *  getBinding      - (id) => binding | null
 *  onSave          - (componentId, label) => void
 *  onSaveAll       - () => void  — bind all selected with default labels
 *  onAssignDashboard - (primaryBindingId, {name, tabs}) => void
 *  onRemove        - (componentId) => void
 *  onDeselect      - (componentId) => void  — remove one from selection
 *  onOpenDashboard - (dashboardId) => void
 *  onClose         - () => void
 *  saving          - boolean
 *  error           - string | null
 */
function ComponentBindingPanel({
  componentIds = [],
  fileId,
  selectedModelName,
  getBinding,
  onSave,
  onSaveAll,
  onAssignDashboard,
  onRemove,
  onDeselect,
  onOpenDashboard,
  onClose,
  saving,
  error,
}) {
  const theme = useSelector((state) => state.themedata.theme);
  const isDark = theme === 'dark';

  // For single-component view: label editing
  const primaryId = componentIds[0] || null;
  const primaryBinding = primaryId ? getBinding(primaryId) : null;
  const [label, setLabel] = useState(primaryBinding?.label || primaryId || '');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (componentIds.length === 1) {
      const b = getBinding(componentIds[0]);
      setLabel(b?.label || componentIds[0] || '');
    }
  }, [componentIds, getBinding]);

  const isMulti = componentIds.length > 1;

  // Count how many selected components already have bindings / dashboards
  const boundCount = componentIds.filter((id) => !!getBinding(id)).length;
  const dashboardCount = componentIds.filter((id) => getBinding(id)?.dashboard_id).length;

  const cardSx = {
    position: 'absolute',
    top: 16,
    right: 16,
    width: isMulti ? 360 : 320,
    maxHeight: 'calc(100% - 32px)',
    overflowY: 'auto',
    zIndex: 10,
    backgroundColor: isDark ? '#1a1a1a' : '#fff',
    border: `1px solid ${isDark ? '#333' : '#ddd'}`,
    borderRadius: 2,
    padding: 2,
    boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
  };

  const mutedColor = isDark ? '#888' : '#666';

  // ── Single-component panel ────────────────────────────────────────────────
  if (!isMulti && primaryId) {
    return (
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#000' }}>
            Component Binding
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" sx={{ color: isDark ? '#aaa' : '#666' }} />
          </IconButton>
        </Box>

        <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 0.5 }}>
          ID: <code style={{ fontSize: 11 }}>{primaryId}</code>
        </Typography>
        <Typography variant="caption" sx={{ color: isDark ? '#666' : '#aaa', display: 'block', mb: 1.5, fontSize: 10 }}>
          Model: {selectedModelName || 'Default (built-in)'}
        </Typography>

        <TextField
          label="Display Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{ style: { color: isDark ? '#fff' : '#000' } }}
          InputLabelProps={{ style: { color: isDark ? '#aaa' : '#666' } }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={() => onSave(primaryId, label)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <LinkIcon />}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          {primaryBinding ? 'Update Binding' : 'Create Binding'}
        </Button>

        {primaryBinding && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 1 }}>
              Dashboard Assignment
            </Typography>
            {primaryBinding.dashboard_id ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label="Dashboard linked"
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<LinkIcon />}
                  onClick={() => onOpenDashboard(primaryBinding.dashboard_id)}
                  sx={{ cursor: 'pointer', flex: 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => onOpenDashboard(primaryBinding.dashboard_id)}
                  sx={{ textTransform: 'none', fontSize: 11 }}
                >
                  Open
                </Button>
              </Box>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Create &amp; Assign Dashboard
                </Button>
                <DTDashboardCreateDialog
                  open={createDialogOpen}
                  componentId={label || primaryId}
                  theme={theme}
                  onClose={() => setCreateDialogOpen(false)}
                  onConfirm={(name, tabs) => {
                    setCreateDialogOpen(false);
                    onAssignDashboard({ name, tabs });
                  }}
                />
              </>
            )}

            <Button
              size="small"
              color="error"
              fullWidth
              onClick={() => onRemove(primaryId)}
              startIcon={<LinkOffIcon />}
              sx={{ mt: 1.5, textTransform: 'none' }}
            >
              Remove Binding
            </Button>
          </>
        )}
      </Box>
    );
  }

  // ── Multi-component panel ─────────────────────────────────────────────────
  return (
    <Box sx={cardSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#000' }}>
          Multi-Component Binding
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" sx={{ color: isDark ? '#aaa' : '#666' }} />
        </IconButton>
      </Box>

      <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 1 }}>
        {componentIds.length} components selected · {boundCount} bound · {dashboardCount} with dashboard
      </Typography>
      <Typography variant="caption" sx={{ color: isDark ? '#555' : '#bbb', display: 'block', mb: 1.5, fontSize: 10 }}>
        Click a selected component again in the 3D view to deselect it.
      </Typography>

      {/* Component chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
        {componentIds.map((id) => {
          const b = getBinding(id);
          const hasDashboard = !!b?.dashboard_id;
          const hasBinding = !!b;
          return (
            <Chip
              key={id}
              label={b?.label || id}
              size="small"
              icon={
                hasDashboard
                  ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />
                  : hasBinding
                  ? <LinkIcon sx={{ fontSize: 14, color: '#008CD7 !important' }} />
                  : <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: `${mutedColor} !important` }} />
              }
              onDelete={() => onDeselect(id)}
              sx={{
                backgroundColor: isDark ? '#2a2a2a' : '#f0f0f0',
                color: isDark ? '#ddd' : '#333',
                fontSize: 11,
                maxWidth: 160,
              }}
            />
          );
        })}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {/* Bind All — ensures every selected component has a binding entry */}
      <Button
        variant="contained"
        size="small"
        fullWidth
        onClick={onSaveAll}
        disabled={saving || boundCount === componentIds.length}
        startIcon={saving ? <CircularProgress size={14} /> : <LinkIcon />}
        sx={{ mb: 1.5, textTransform: 'none' }}
      >
        {boundCount === componentIds.length ? 'All Components Bound' : `Bind All (${componentIds.length - boundCount} unbound)`}
      </Button>

      <Divider sx={{ mb: 1.5 }} />

      {/* Dashboard section */}
      <Typography variant="caption" sx={{ color: mutedColor, display: 'block', mb: 1 }}>
        Shared Dashboard Assignment
      </Typography>

      {dashboardCount > 0 && (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: 11, py: 0.5 }}>
          {dashboardCount === componentIds.length
            ? 'All components already have dashboards assigned.'
            : `${dashboardCount} of ${componentIds.length} components already have dashboards.`}
        </Alert>
      )}

      <Button
        variant="outlined"
        size="small"
        fullWidth
        onClick={() => setCreateDialogOpen(true)}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        Create &amp; Assign Shared Dashboard
      </Button>
      <Typography variant="caption" sx={{ color: isDark ? '#555' : '#bbb', display: 'block', fontSize: 10 }}>
        Creates one dashboard and links all {componentIds.length} selected components to it.
      </Typography>

      <DTDashboardCreateDialog
        open={createDialogOpen}
        componentId={componentIds.map((id) => getBinding(id)?.label || id).join(', ')}
        theme={theme}
        onClose={() => setCreateDialogOpen(false)}
        onConfirm={(name, tabs) => {
          setCreateDialogOpen(false);
          onAssignDashboard({ name, tabs });
        }}
      />
    </Box>
  );
}

export default React.memo(ComponentBindingPanel);
